import React, { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import {
  launchableContentTabsListClassName,
  type LaunchableContentTabProps,
} from "components/Launchables/DeployRedesign/shared/content/tabs/LaunchableContentTab";

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
  <pre className="bg-zinc-900 border border-zinc-700 rounded-md px-4 py-3 text-sm text-white/85 font-mono overflow-x-auto whitespace-pre-wrap">
    {children}
  </pre>
);

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-white/85 underline"
  >
    {children}
  </a>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h3 className="text-white/85 text-sm font-semibold mt-6 mb-2 first:mt-0">
    {children}
  </h3>
);

const Paragraph: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <p className={`text-white/85 text-sm leading-relaxed ${className ?? ""}`}>
    {children}
  </p>
);

const OverviewTab: React.FC = () => (
  <div className="flex flex-col gap-5">
    <div>
      <Paragraph>
        NVIDIA NemoClaw is a collection of open source reference blueprints that
        simplify running always-on assistants more safely. It installs the{" "}
        <ExternalLink href="https://github.com/NVIDIA/OpenShell">
          NVIDIA OpenShell
        </ExternalLink>{" "}
        secure runtime, available in alpha, which provides additional security
        for running autonomous agents.
      </Paragraph>
    </div>

    <div>
      <Paragraph>
        Brev provisions a long-running cloud VM for your NemoClaw agent and
        handles the installation automatically — no separate hardware or manual
        setup required.
      </Paragraph>
    </div>

    <Paragraph>
      NemoClaw adds guided onboarding, a hardened blueprint, state management,
      messaging bridges, routed inference, and layered protection on top of the{" "}
      <ExternalLink href="https://github.com/NVIDIA/OpenShell">
        NVIDIA OpenShell
      </ExternalLink>{" "}
      runtime. For the full feature list, refer to{" "}
      <ExternalLink href="https://docs.nvidia.com/nemoclaw/latest/about/overview.html">
        Overview
      </ExternalLink>
      . For the system diagram, component model, and blueprint lifecycle, refer
      to{" "}
      <ExternalLink href="https://docs.nvidia.com/nemoclaw/latest/about/how-it-works.html">
        How It Works
      </ExternalLink>{" "}
      and{" "}
      <ExternalLink href="https://docs.nvidia.com/nemoclaw/latest/reference/architecture.html">
        Architecture
      </ExternalLink>
      .
    </Paragraph>

    <div>
      <SectionHeading>Hardware Requirements</SectionHeading>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-white/85 border border-zinc-800 rounded-md">
          <thead>
            <tr className="border-b border-zinc-800 text-left">
              <th className="px-3 py-2 text-white/85 font-medium">Resource</th>
              <th className="px-3 py-2 text-white/85 font-medium">Minimum</th>
              <th className="px-3 py-2 text-white/85 font-medium">
                Recommended
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              ["CPU", "4 vCPU", "4+ vCPU"],
              ["RAM", "8 GB", "16 GB"],
              ["Disk", "20 GB free", "40 GB free"],
            ].map(([resource, min, rec]) => (
              <tr
                key={resource}
                className="border-b border-zinc-800 last:border-0"
              >
                <td className="px-3 py-2 text-white/85">{resource}</td>
                <td className="px-3 py-2">{min}</td>
                <td className="px-3 py-2">{rec}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paragraph className="mt-2">
        The sandbox image is approximately 2.4 GB compressed. On machines with
        less than 8 GB of RAM, configuring at least 8 GB of swap can work around
        OOM issues at the cost of slower performance.
      </Paragraph>
    </div>

    <div>
      <SectionHeading>Software Requirements</SectionHeading>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-white/85 border border-zinc-800 rounded-md">
          <thead>
            <tr className="border-b border-zinc-800 text-left">
              <th className="px-3 py-2 text-white/85 font-medium">
                Dependency
              </th>
              <th className="px-3 py-2 text-white/85 font-medium">Version</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Linux", "Ubuntu 22.04 LTS or later"],
              ["Node.js", "22.16 or later"],
              ["npm", "10 or later"],
              ["Container runtime", "Supported runtime installed and running"],
              ["OpenShell", "Installed"],
            ].map(([dep, ver]) => (
              <tr key={dep} className="border-b border-zinc-800 last:border-0">
                <td className="px-3 py-2 text-white/85">{dep}</td>
                <td className="px-3 py-2">{ver}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    <div>
      <SectionHeading>Getting Started</SectionHeading>
      <Paragraph>
        Your Brev instance comes with NemoClaw pre-installed, so you don&apos;t
        need to follow the installation steps yourself. For reference, the full
        installation instructions, uninstall steps, and setup documentation are
        available in the{" "}
        <ExternalLink href="https://github.com/NVIDIA/NemoClaw">
          NemoClaw GitHub repository
        </ExternalLink>
        .
      </Paragraph>
    </div>
  </div>
);

const whyBrevFeatures = [
  {
    title: "Isolated from your personal computer",
    description:
      "Running NemoClaw on a cloud VM limits agent access to what you explicitly grant. Your laptop's files, credentials, and personal data remain inaccessible to the agent. Everything is pre-installed on a clean, isolated machine, so you control exactly what NemoClaw can reach.",
  },
  {
    title: "Low-cost cloud VM, billed hourly",
    description:
      "NemoClaw agents need a persistent cloud instance to run tasks and write code. Brev provisions a low-cost CPU instance and bills only for compute while it's running — no upfront costs, no idle hardware, and no maintenance.",
  },
];

const WhyBrevTab: React.FC = () => (
  <div className="flex flex-col gap-5">
    <div className="flex flex-col gap-3">
      {whyBrevFeatures.map((feature) => (
        <div key={feature.title}>
          <SectionHeading>{feature.title}</SectionHeading>
          <Paragraph>{feature.description}</Paragraph>
        </div>
      ))}
    </div>
  </div>
);

const docLinks = [
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/about/overview.html",
    title: "Overview",
    description: "What NemoClaw does and how it fits together.",
  },
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/about/how-it-works.html",
    title: "How It Works",
    description: "Plugin, blueprint, sandbox lifecycle, and protection layers.",
  },
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/reference/architecture.html",
    title: "Architecture",
    description:
      "Plugin structure, blueprint lifecycle, sandbox environment, and host-side state.",
  },
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/reference/inference-profiles.html",
    title: "Inference Profiles",
    description:
      "Supported providers, validation, and routed inference configuration.",
  },
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/reference/network-policies.html",
    title: "Network Policies",
    description: "Baseline rules, operator approval flow, and egress control.",
  },
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/network-policy/customize-network-policy.html",
    title: "Customize Network Policy",
    description: "Static and dynamic policy changes, presets.",
  },
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/security/best-practices.html",
    title: "Security Best Practices",
    description:
      "Controls reference, risk framework, and posture profiles for sandbox security.",
  },
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/deployment/sandbox-hardening.html",
    title: "Sandbox Hardening",
    description:
      "Container security measures, capability drops, process limits.",
  },
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/reference/commands.html",
    title: "CLI Commands",
    description: "Full NemoClaw CLI command reference.",
  },
  {
    href: "https://docs.nvidia.com/nemoclaw/latest/reference/troubleshooting.html",
    title: "Troubleshooting",
    description: "Common issues and resolution steps.",
  },
];

const DocumentationTab: React.FC = () => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm text-white/85 border border-zinc-800 rounded-md">
      <thead>
        <tr className="border-b border-zinc-800 text-left">
          <th className="px-3 py-2 text-white/85 font-medium">Page</th>
          <th className="px-3 py-2 text-white/85 font-medium">Description</th>
        </tr>
      </thead>
      <tbody>
        {docLinks.map((link) => (
          <tr
            key={link.href}
            className="border-b border-zinc-800 last:border-0"
          >
            <td className="px-3 py-2 whitespace-nowrap">
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/85 underline"
              >
                {link.title}
              </a>
            </td>
            <td className="px-3 py-2">{link.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CLITab: React.FC = () => {
  const [selectedOS, setSelectedOS] = useState<"darwin" | "linux" | "windows">(
    "darwin",
  );

  const installCommands: Record<"darwin" | "linux" | "windows", string> = {
    darwin: "brew install brevdev/homebrew-brev/brev",
    linux: `bash -c "$(curl -fsSL https://raw.githubusercontent.com/brevdev/brev-cli/main/bin/install-latest.sh)"`,
    windows: `bash -c "$(curl -fsSL https://raw.githubusercontent.com/brevdev/brev-cli/main/bin/install-latest.sh)"`,
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Brev CLI install */}
      <div>
        <SectionHeading>Install the Brev CLI</SectionHeading>
        <Paragraph className="mb-3">
          The Brev CLI lets you connect to your NemoClaw instance directly from
          your terminal using{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            brev shell
          </code>
          .
        </Paragraph>
        <div className="flex gap-2 mb-3">
          {(["darwin", "linux", "windows"] as const).map((os) => (
            <button
              key={os}
              onClick={() => setSelectedOS(os)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                selectedOS === os
                  ? "bg-zinc-700 text-white/85"
                  : "bg-zinc-800/50 text-white/85"
              }`}
            >
              {os === "darwin"
                ? "macOS"
                : os === "linux"
                  ? "Linux"
                  : "Windows (WSL)"}
            </button>
          ))}
        </div>
        <CodeBlock>{installCommands[selectedOS]}</CodeBlock>
        {selectedOS === "windows" && (
          <Paragraph className="mt-2">
            Make sure you have WSL installed and configured, virtualization
            enabled in your BIOS, and Ubuntu 20.04 installed from the Microsoft
            Store.
          </Paragraph>
        )}
      </div>

      {/* brev shell */}
      <div>
        <SectionHeading>Connect to your instance</SectionHeading>
        <Paragraph className="mb-2">
          After your NemoClaw instance is running, log in and open a shell:
        </Paragraph>
        <CodeBlock>{"brev login\nbrev shell <instance-name>"}</CodeBlock>
        <Paragraph className="mt-2">
          Replace{" "}
          <code className="text-white/85 bg-zinc-800 px-1 rounded">
            &lt;instance-name&gt;
          </code>{" "}
          with the name of your Brev instance. Once connected, you are inside
          the VM where NemoClaw is running.
        </Paragraph>
      </div>

      {/* Divider + alternative note */}
      <div className="border border-white/10 rounded-lg p-4 bg-white/[0.02]">
        <p className="text-white/85 text-sm leading-relaxed">
          <span className="text-white/85 font-medium">Tip:</span> The NemoClaw{" "}
          <span className="text-white/85">secure link</span> on your Brev
          instance provides a browser-based interface to interact with your
          agent. The commands below are an alternative for users who prefer
          working directly in the terminal.
        </p>
      </div>

      {/* Chat with the Agent */}
      <div>
        <SectionHeading>Chat with the agent via CLI</SectionHeading>
        <Paragraph className="mb-2">
          Connect to the sandbox, then chat with the agent through the TUI or
          the CLI.
        </Paragraph>
        <CodeBlock>{"nemoclaw my-assistant connect"}</CodeBlock>
        <Paragraph className="mt-3 mb-2">
          In the sandbox shell, open the OpenClaw terminal UI and start a chat:
        </Paragraph>
        <CodeBlock>{"openclaw tui"}</CodeBlock>
        <Paragraph className="mt-3 mb-2">
          Alternatively, send a single message and print the response:
        </Paragraph>
        <CodeBlock>
          {'openclaw agent --agent main --local -m "hello" --session-id test'}
        </CodeBlock>
      </div>
    </div>
  );
};

const CommunityTab: React.FC = () => (
  <div className="flex flex-col gap-6">
    <div>
      <SectionHeading>Join the community</SectionHeading>
      <Paragraph className="mb-3">
        Connect with other NemoClaw users, ask questions, share feedback, and
        report issues.
      </Paragraph>
      <div className="flex flex-col gap-2">
        {[
          {
            href: "https://discord.gg/XFpfPv9Uvx",
            label: "Discord",
            description: "Chat with the community in real time.",
          },
          {
            href: "https://github.com/NVIDIA/NemoClaw/discussions",
            label: "GitHub Discussions",
            description: "Ask questions and share ideas.",
          },
          {
            href: "https://github.com/NVIDIA/NemoClaw/issues",
            label: "GitHub Issues",
            description: "Report bugs and request features.",
          },
        ].map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-white/10 rounded-lg p-4 flex flex-col gap-1 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
          >
            <span className="text-white/85 text-sm font-medium hover:underline">
              {link.label}
            </span>
            <span className="text-white/85 text-sm">{link.description}</span>
          </a>
        ))}
      </div>
    </div>

    <div>
      <SectionHeading>Contributing</SectionHeading>
      <Paragraph>
        We welcome contributions. See{" "}
        <ExternalLink href="https://github.com/NVIDIA/NemoClaw/blob/main/CONTRIBUTING.md">
          CONTRIBUTING.md
        </ExternalLink>{" "}
        for development setup, coding standards, and the PR process.
      </Paragraph>
    </div>

    <div>
      <SectionHeading>Security</SectionHeading>
      <Paragraph>
        NVIDIA takes security seriously. If you discover a vulnerability in
        NemoClaw,{" "}
        <span className="text-white/85 font-medium">
          do not open a public issue.
        </span>{" "}
        Instead, use one of the private reporting channels:
      </Paragraph>
      <ul className="mt-2 flex flex-col gap-1 text-sm text-white/85 list-disc list-inside">
        <li>
          Submit a report through the{" "}
          <ExternalLink href="https://www.nvidia.com/en-us/security/report-vulnerability/">
            NVIDIA Vulnerability Disclosure Program
          </ExternalLink>
          .
        </li>
        <li>
          Email{" "}
          <ExternalLink href="mailto:psirt@nvidia.com">
            psirt@nvidia.com
          </ExternalLink>{" "}
          encrypted with the{" "}
          <ExternalLink href="https://www.nvidia.com/en-us/security/pgp-key">
            NVIDIA PGP key
          </ExternalLink>
          .
        </li>
        <li>
          Use{" "}
          <ExternalLink href="https://docs.github.com/en/code-security/how-tos/report-and-fix-vulnerabilities/configure-vulnerability-reporting/configuring-private-vulnerability-reporting-for-a-repository">
            GitHub&apos;s private vulnerability reporting
          </ExternalLink>{" "}
          on this repository.
        </li>
      </ul>
    </div>
  </div>
);

const LegalTab: React.FC = () => (
  <div className="flex flex-col gap-6">
    <div>
      <SectionHeading>Notice and Disclaimer</SectionHeading>
      <Paragraph>
        This software automatically retrieves, accesses or interacts with
        external materials. Those retrieved materials are not distributed with
        this software and are governed solely by separate terms, conditions and
        licenses. You are solely responsible for finding, reviewing and
        complying with all applicable terms, conditions, and licenses, and for
        verifying the security, integrity and suitability of any retrieved
        materials for your specific use case. This software is provided &quot;AS
        IS&quot;, without warranty of any kind. The author makes no
        representations or warranties regarding any retrieved materials, and
        assumes no liability for any losses, damages, liabilities or legal
        consequences from your use or inability to use this software or any
        retrieved materials. Use this software and the retrieved materials at
        your own risk.
      </Paragraph>
    </div>

    <div>
      <SectionHeading>License</SectionHeading>
      <Paragraph>
        Apache 2.0. See{" "}
        <ExternalLink href="https://github.com/NVIDIA/NemoClaw/blob/main/LICENSE">
          LICENSE
        </ExternalLink>
        .
      </Paragraph>
    </div>
  </div>
);

const NemoClawSkin: React.FC<LaunchableContentTabProps> = ({
  activeTab,
  onActiveTabChange,
  leadingTabs = [],
  trailingTabs = [],
}) => (
  <div className="flex w-full flex-col gap-4">
    <Tabs
      value={activeTab}
      onValueChange={onActiveTabChange}
      defaultValue={activeTab ? undefined : "overview"}
    >
      <TabsList variant="line" className={launchableContentTabsListClassName}>
        {leadingTabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="why-brev">Why Brev?</TabsTrigger>
        <TabsTrigger value="documentation">Documentation</TabsTrigger>
        <TabsTrigger value="cli">CLI</TabsTrigger>
        <TabsTrigger value="community">Community</TabsTrigger>
        <TabsTrigger value="legal">Legal</TabsTrigger>
        {trailingTabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {leadingTabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value}>
          {tab.content}
        </TabsContent>
      ))}
      <TabsContent value="overview">
        <OverviewTab />
      </TabsContent>
      <TabsContent value="why-brev">
        <WhyBrevTab />
      </TabsContent>
      <TabsContent value="documentation">
        <DocumentationTab />
      </TabsContent>
      <TabsContent value="cli">
        <CLITab />
      </TabsContent>
      <TabsContent value="community">
        <CommunityTab />
      </TabsContent>
      <TabsContent value="legal">
        <LegalTab />
      </TabsContent>
      {trailingTabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value}>
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  </div>
);

export default NemoClawSkin;
